import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Paginator } from "primereact/paginator";
import { Tag } from "primereact/tag";
import Loading from "../../Components/Loading";
import CustomDataTable from "../../Components/CustomDataTable";
import EnrichmentDetailCards from "./EnrichmentDetailCards";
import { useDateFormatter } from "../../Components/FormatDateTime";
import { removePlusFromNum } from "../../Components/CleanInputs";
import DefMembershipActions from "../../Redux/actions/defMembership-actions";
import BusinessCustomersActions from "../../Redux/transition/customers/business-customer.reducer";
import "../../Styles/pages/customers.scss";
import "../../Styles/pages/product.scss";
import "../../Styles/pages/products.scss";
import "../../Styles/pages/dashboard.scss";
import "../../Styles/components/tags.scss";

const ENRICHMENT_DETAIL_MOBILE_BREAKPOINT = 1050;

const formatCell = (value) => (value != null && value !== "" ? value : "--");

const getSegmentLabel = (segment) => {
    if (segment == null || segment === "") {
        return null;
    }
    if (typeof segment === "object") {
        return segment?.name ?? segment?.label ?? null;
    }
    return segment;
};

const normalizeTags = (tags) => {
    if (!tags) {
        return [];
    }
    if (Array.isArray(tags)) {
        return tags
            .map((tag) => {
                if (typeof tag === "string") {
                    return { id: tag, name: tag };
                }
                const name = tag?.name ?? tag?.label;
                if (!name) {
                    return null;
                }
                return { id: tag?.id ?? name, name };
            })
            .filter(Boolean);
    }
    if (typeof tags === "string") {
        return tags
            .replace(/[{}]/g, "")
            .split(/[,|/]/)
            .map((tag) => tag.replace(/"/g, "").trim())
            .filter(Boolean)
            .map((name) => ({ id: name, name }));
    }
    return [];
};

const getItemStatusKey = (row) => {
    if (typeof row?.unmatched === "boolean") {
        return row.unmatched ? "unmatched" : "matched";
    }
    if (row?.status) {
        return String(row.status).toLowerCase();
    }
    if (!row?.processedOn) {
        return "pending";
    }
    return null;
};

const EnrichmentDetail = () => {
    const { t } = useTranslation("common");
    const { formatDateTime } = useDateFormatter();
    const navigate = useNavigate();
    const { requestId } = useParams();
    const location = useLocation();
    const enrichment = location.state?.enrichment;

    const [state, setState] = useState({
        items: [],
        first: 0,
        count: null,
        size: 20,
        sort: "id,asc",
    });
    const [isMobileStack, setIsMobileStack] = useState(
        () => typeof window !== "undefined" && window.innerWidth <= ENRICHMENT_DETAIL_MOBILE_BREAKPOINT
    );

    const defMembership = useSelector((s) => s.defMembership.defMembership);
    const {
        customerEnrichmentItems,
        fetchingCustomerEnrichmentItems,
        customerEnrichmentItemsError,
    } = useSelector((s) => s.businessCustomers);

    const dispatch = useDispatch();
    const getMembership = useCallback(() => dispatch(DefMembershipActions.defMembershipRequest()), [dispatch]);
    const getCustomerEnrichmentItems = useCallback(
        (businessId, reqId, options) =>
            dispatch(BusinessCustomersActions.customerEnrichmentItemsRequest(businessId, reqId, options)),
        [dispatch]
    );

    const isAdminOrOwner = defMembership?.role === "OWNER" || defMembership?.role === "ADMIN";
    const summary = enrichment?.requestId != null && String(enrichment.requestId) === String(requestId)
        ? enrichment
        : { requestId };

    const fetchItems = useCallback(
        (page, rows = state.size) => {
            if (!defMembership?.business?.id || !requestId) {
                return;
            }
            getCustomerEnrichmentItems(defMembership.business.id, requestId, {
                page,
                size: rows,
                sort: state.sort,
            });
        },
        [defMembership?.business?.id, getCustomerEnrichmentItems, requestId, state.size, state.sort]
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${ENRICHMENT_DETAIL_MOBILE_BREAKPOINT}px)`);
        const onBreakpointChange = (event) => setIsMobileStack(event.matches);
        onBreakpointChange(mediaQuery);
        mediaQuery.addEventListener("change", onBreakpointChange);
        return () => mediaQuery.removeEventListener("change", onBreakpointChange);
    }, []);

    useEffect(() => {
        if (!defMembership) {
            getMembership();
        }
    }, [defMembership, getMembership]);

    useEffect(() => {
        if (defMembership && !isAdminOrOwner) {
            navigate("/crm/customers", { replace: true });
        }
    }, [defMembership, isAdminOrOwner, navigate]);

    useEffect(() => {
        if (defMembership?.business?.id && isAdminOrOwner && requestId) {
            fetchItems(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defMembership?.business?.id, isAdminOrOwner, requestId]);

    useEffect(() => {
        if (customerEnrichmentItems) {
            setState((prev) => ({
                ...prev,
                items: customerEnrichmentItems.content ?? [],
                count: customerEnrichmentItems.totalElements ?? 0,
            }));
        }
    }, [customerEnrichmentItems]);

    const onPageChange = (e) => {
        const { page, first, rows } = e;
        fetchItems(page, rows);
        setState((prev) => ({
            ...prev,
            first,
            size: rows,
        }));
    };

    const itemStatusBody = (rowData) => {
        const statusKey = getItemStatusKey(rowData);
        if (!statusKey) {
            return formatCell(rowData.status);
        }
        const labelKey = `business_customers.enrichment_item_status_${statusKey}`;
        const label = t(labelKey, { defaultValue: formatCell(rowData.status) });
        return (
            <span className={`enrichment-status enrichment-status-${statusKey}`}>
                {label}
            </span>
        );
    };

    const segmentBody = (row) => {
        const label = getSegmentLabel(row.segment);
        if (!label) {
            return <div className="text-left" />;
        }
        return (
            <div className="text-left segment-main-div">
                <div className="segment-tag-div">
                    <span className="segment-class">{label}</span>
                </div>
            </div>
        );
    };

    const tagsBody = (row) => {
        const tags = normalizeTags(row.tags);
        if (!tags.length) {
            return <div className="text-left" />;
        }
        return (
            <div className="text-left wrapping-div">
                {tags.map((tag) => (
                    <Tag key={tag.id} className="tag-div-main">
                        <div className="tag_div">
                            <span className="tag-class">{tag.name}</span>
                        </div>
                    </Tag>
                ))}
            </div>
        );
    };

    const desktopColumns = [
        {
            field: "customerNum",
            header: t("common.reference"),
            body: (row) => formatCell(row.customerNum),
            headerClassName: "customer_headers datatable-header-default",
        },
        {
            field: "reveknewReference",
            header: t("business_customers.customer_id"),
            body: (row) => (
                row.reveknewReference
                    ? <span className="customerId">{row.reveknewReference}</span>
                    : formatCell(null)
            ),
            headerClassName: "customer_headers datatable-header-default",
        },
        {
            field: "name",
            header: t("common.name"),
            body: (row) => <div className="text-left">{formatCell(row.name)}</div>,
            bodyClassName: "text-left",
            headerClassName: "customer_headers datatable-header-default",
        },
        {
            field: "phoneNo",
            header: t("common.phone_no"),
            body: (row) => (
                row.phoneNo
                    ? <span className="customer_data">{formatCell(removePlusFromNum(row.phoneNo))}</span>
                    : formatCell(null)
            ),
            headerClassName: "customer_headers datatable-header-default",
        },
        {
            field: "segment",
            header: t("business_customers.segment"),
            body: segmentBody,
            headerClassName: "customer_headers datatable-header-default",
        },
        {
            field: "tags",
            header: t("business_customers.tags"),
            body: tagsBody,
            headerClassName: "customer_headers datatable-header-default",
        },
        {
            field: "unmatched",
            header: t("business_customers.enrichment_status"),
            body: itemStatusBody,
            headerClassName: "customer_headers datatable-header-default",
        },
        {
            field: "processedOn",
            header: t("business_customers.enrichment_item_processed_on"),
            body: (row) => (row.processedOn ? formatDateTime(row.processedOn) : formatCell(null)),
            headerClassName: "customer_headers datatable-header-default",
        },
    ];

    const enrichmentMobileSummary = (rowData) => (
        <div className="enrichment-detail-mobile-summary">
            {desktopColumns.map((col, index) => {
                const isLast = index === desktopColumns.length - 1;
                return (
                    <div
                        key={col.field}
                        className={`enrichment-detail-mobile-section${isLast ? " enrichment-detail-mobile-section-last" : ""}`}
                    >
                        {col.header ? (
                            <span className="enrichment-detail-mobile-label">{col.header}</span>
                        ) : null}
                        {col.body(rowData)}
                    </div>
                );
            })}
        </div>
    );

    const columns = isMobileStack
        ? [{
            field: "mobileSummary",
            header: t("common.reference"),
            body: enrichmentMobileSummary,
            className: "enrichment-detail-mobile-summary-column",
        }]
        : desktopColumns;

    if (!defMembership || !isAdminOrOwner) {
        return null;
    }

    if (fetchingCustomerEnrichmentItems && !state.items.length && !customerEnrichmentItemsError) {
        return <Loading />;
    }

    return (
        <div className="customerContent enrichment-detail-screen">
            <div className="enrichment-detail-page-header">
                <p className="title-text mb-0">
                    {t("business_customers.enrichment_detail_title", {
                        requestId: summary.requestId ?? requestId,
                    })}
                </p>
                <p className="sub_text mb-0">{t("business_customers.enrichment_detail_description")}</p>
            </div>
            <EnrichmentDetailCards
                t={t}
                summary={summary}
                requestId={requestId}
                formatDateTime={formatDateTime}
            />

            {customerEnrichmentItemsError ? (
                <div className="enrichment-detail-error p-3">
                    <p className="mb-2">{t("business_customers.enrichment_items_error")}</p>
                    <Link to="/crm/enrichment-requests">{t("business_customers.enrichment_back_to_list")}</Link>
                </div>
            ) : (
                <div className="customers-screen enrichment-detail-table">
                    <CustomDataTable
                        key={`enrichment-items-${isMobileStack}`}
                        value={state.items}
                        paginator={false}
                        responsiveLayout="stack"
                        columns={columns}
                        dataKey="id"
                        scrollable={!isMobileStack}
                        tableClassName="enrichment-data-table enrichment-items-table"
                        rowClassName={() => "subscribers-data-table enrichment-list-row"}
                        loading={fetchingCustomerEnrichmentItems}
                        emptyMessage={t("business_customers.enrichment_no_items")}
                    />
                    <Paginator
                        rows={state.size}
                        totalRecords={state.count ?? 0}
                        first={state.first}
                        onPageChange={onPageChange}
                        className="custom-paginator enrichment-detail-paginator"
                    />
                </div>
            )}
        </div>
    );
};

export default EnrichmentDetail;
